import { useCallback, useEffect, useMemo, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { DateField } from '../../../../components/inputs/DateField';
import {
  TdDate,
  TdInput,
  TdMoney,
  TdNumber,
  TdSeamless,
  TdSelect,
} from '../../../../components/inline-add-table/cells';
import { Modal } from '../../../../components/Modal';
import { MultiSelect } from '../../../../components/inputs/MultiSelect';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Stepper } from '../../../../components/Stepper';
import { useColumnWidths } from '../../../../components/table/useColumnWidths';
import { Tooltip } from '../../../../components/Tooltip';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { focusValidationError } from '../../../../lib/validationFocus';
import { goodsReceiptsApi } from '../goodsReceiptApi';
import type {
  CreateGoodsReceiptLine,
  GoodsReceiptDetail,
  GoodsReceiptSource,
  ReceivableOrderLine,
  ReceivableOrderOption,
} from '../goodsReceiptTypes';
import {
  GOODS_RECEIPT_SOURCE,
  GOODS_RECEIPT_SOURCE_LABEL,
  PURCHASE_ORDER_STAGE,
  usesPurchaseOrder,
} from '../goodsReceiptTypes';
import { inventoryItemsApi, storageFacilitiesApi } from '../inventoryApi';
import type {
  InventoryItemKind,
  InventoryItemOption,
  StorageFacilityOption,
} from '../inventoryTypes';
import { ITEM_KIND } from '../inventoryTypes';
import { sampleDelivery } from '../utils/sampleDelivery';

/**
 * <b>SOURCE STANDS ALONE AS STEP ONE — product call, 05 Oct 2026</b>, in the reader's own words
 * against a screenshot of the radio group: *make this 1st step*.
 *
 * It shared step one with the counterparty until then, and this comment argued for that: *"a
 * step holding one radio group would be a rail promising a stage of work that is three options,
 * which is the call that took the Identity step off the item form."* That is a real argument and
 * it is kept rather than deleted — what overruled it is that the source decides the SHAPE of
 * every step after it, so it is a decision the reader makes before the form has a shape at all,
 * not the first field of a form that already does.
 *
 * <b>THE SECOND STEP IS NAMED FOR WHAT IT ASKS, which differs per source</b> — the step list is
 * DERIVED, the way `<InventoryItemModal>` derives its own from the item kind and the issuance
 * wizard derives its from the method. A reader on a donation meets `Donor`, not a generic word
 * covering three things it is not.
 *
 * <b>The COUNT is five whichever source is chosen</b>, so there is no step to clamp: switching
 * source relabels step two rather than removing it, and a reader standing on step three stays
 * where they are.
 */
const STEP_TWO: Record<GoodsReceiptSource, string> = {
  [GOODS_RECEIPT_SOURCE.purchaseOrder]: 'Source PO',
  [GOODS_RECEIPT_SOURCE.donation]: 'Donor',
  [GOODS_RECEIPT_SOURCE.stockTransfer]: 'From store',
};

function stepsFor(source: GoodsReceiptSource): readonly string[] {
  return ['Source', STEP_TWO[source], 'Line Items', 'Delivery Details', 'Summary'];
}

/**
 * The step a key names, by POSITION rather than by label — because step two's label moves with
 * the source and a render branch keyed on the word would go dark the moment it did.
 */
const STEP = {
  source: 0,
  counterparty: 1,
  lines: 2,
  delivery: 3,
  summary: 4,
} as const;

/**
 * ONE ROW OF THE GRID, whichever way its source names the item.
 *
 * A purchase-order receipt maps its order lines into this; a donation and a transfer map the
 * items the reader picked. Everything below the first step reads THIS rather than either —
 * which is what keeps the grid, the summary and the generator identical for all three sources
 * instead of branching eleven times.
 *
 * The fields an ORDER supplies are nullable, because the other two sources have no order: there
 * is nothing outstanding to cap the receive box at, no agreed price to seed the cost from, and
 * no requisition number to show.
 */
export interface GridLine {
  /** The row's identity — the order line where there is one, else the item. */
  key: string;
  purchaseOrderItemId: string | null;
  inventoryItemId: string;
  itemName: string;
  sku: string;
  unitCode: string;
  kind: InventoryItemKind;
  tracksSerial: boolean;
  tracksExpiry: boolean;
  sitsSomewhere: boolean;
  /** What is still outstanding ON THE ORDER. NULL where there is none, and then uncapped. */
  openQuantity: number | null;
  /** The order's agreed price, which seeds the cost cell. NULL where there is no order. */
  unitCost: number | null;
  purchaseRequestNumber: string | null;
}

/** An order line, as the grid reads it. */
function gridLineOfOrder(line: ReceivableOrderLine): GridLine {
  return {
    key: line.purchaseOrderItemId,
    purchaseOrderItemId: line.purchaseOrderItemId,
    inventoryItemId: line.inventoryItemId,
    itemName: line.itemName,
    sku: line.sku,
    unitCode: line.unitCode,
    kind: line.kind,
    tracksSerial: line.tracksSerial,
    tracksExpiry: line.tracksExpiry,
    sitsSomewhere: line.sitsSomewhere,
    openQuantity: line.openQuantity,
    unitCost: line.unitCost,
    purchaseRequestNumber: line.purchaseRequestNumber,
  };
}

/**
 * A picked item, as the grid reads it.
 *
 * `sitsSomewhere` is derived from the KIND rather than sent, because the options feed answers
 * what the item IS and this is the one thing the grid needs that it does not carry. It is the
 * frontend twin of `InventoryItemKinds.SitsSomewhere`, and a licence is the one kind that
 * sits nowhere.
 */
function gridLineOfItem(item: InventoryItemOption): GridLine {
  return {
    key: item.id,
    purchaseOrderItemId: null,
    inventoryItemId: item.id,
    itemName: item.name,
    sku: item.sku,
    unitCode: item.unitCode,
    kind: item.kind,
    tracksSerial: item.tracksSerial,
    tracksExpiry: item.tracksExpiry,
    sitsSomewhere: item.kind !== ITEM_KIND.subscription,
    openQuantity: null,
    unitCost: null,
    purchaseRequestNumber: null,
  };
}

/** One row of the line grid, as the reader is filling it in. */
export interface DraftLine {
  /** The row’s identity, matching its `GridLine`. */
  key: string;
  purchaseOrderItemId: string | null;
  inventoryItemId: string;
  /** Whether the reader ticked it. An unticked row is SENT with a quantity of zero. */
  selected: boolean;
  quantityReceived: number | null;
  /**
   * The DOCK's unusable breakdown — the FIRST of the two inspections.
   *
   * GOOD is not here because it is not asked for: it is received minus these three, derived
   * during render and derived again on the server, so the two cannot disagree and no request can
   * state a good count that contradicts its own breakdown.
   */
  quantityExpired: number | null;
  quantityDamaged: number | null;
  quantityWrongItem: number | null;
  storageFacilityId: string | null;
  expiryDate: string | null;
  unitCost: number | null;
  batchNumber: string | null;
  /** One per unit on a serialized line. */
  serials: string[];
  notes: string | null;
}

/** What the dock found unusable on a row, across the three causes. */
function unusableOf(row: DraftLine): number {
  return (row.quantityExpired ?? 0) + (row.quantityDamaged ?? 0) + (row.quantityWrongItem ?? 0);
}

/**
 * What the dock passed — received minus the three causes.
 *
 * <b>DERIVED during render, never held in state.</b> It is a pure function of four boxes on the
 * row, so storing it would leave a stale figure on screen for a paint after every keystroke —
 * the rule the code suggester and the date field's age segment both already follow. It is
 * derived on the SERVER the same way, which is what stops the two disagreeing.
 */
function goodOf(row: DraftLine): number {
  return (row.quantityReceived ?? 0) - unusableOf(row);
}

/**
 * The line grid's columns, in order.
 *
 * <b>Declared rather than written into the markup, because the widths are now DRAGGABLE</b> and
 * three places have to agree about them: the `<colgroup>` that sizes the table, the header cells
 * that carry the resize handles, and the persisted layout. One array is what keeps them in step.
 *
 * <b>The key is positional and the `resizeKey` is VERSIONED</b> — stored widths are saved by
 * column key, so a grid that changes which columns it has must not hand a reader's old layout to
 * a different set. The `-v2` is that version, and it moves again the next time a column is added
 * or removed.
 *
 * <b>THE DEFAULTS SUM TO JUST UNDER THE MODAL</b>, deliberately — <b>1,044px</b> plus the 40px
 * tick gutter against `3xl`'s 1,152px, which is the widest size that is not fullscreen.
 *
 * Four columns were added here and the first pass simply let the table overflow: Unit cost and
 * Notes sat past the right edge, reachable only by scrolling a bar that is invisible at rest. A
 * reader filling a grid left to right does not discover a column they cannot see, and LOOKING at
 * the rendered table is the only thing that showed it — the markup is identical either way. The
 * second pass clipped the Expiry input to `mm/dd/yyy`, which is the same lesson one round later:
 * a date cell needs room for the native control AND its calendar button.
 *
 * Trimmed rather than left to scroll BECAUSE the columns are draggable now: a reader who wants a
 * wider Item column takes it, and the layout persists. That is the better trade than hiding two
 * columns by default. <b>A twelfth column means re-measuring this sum, not appending to it.</b>
 */
type LineColumn = {
  key: string;
  label: string;
  width: number;
  align: 'left' | 'right';
  band: string | null;
  /**
   * Marked `*` on the heading — <b>only while at least one line on the grid actually draws a
   * control for it</b>.
   *
   * <b>DECLARED ON THE COLUMN, never as a second list of required keys.</b> A set beside this
   * array is the shape a new column goes missing from — the failure `AGING_COLUMN_KEYS` and
   * `headerGroups` each name — so the heading and the obligation are one declaration.
   *
   * <b>IT IS A PREDICATE BECAUSE THE OBLIGATION IS PER ROW</b>, which makes these the first
   * starred columns in the product that are not required on every line. Storage is asked of a
   * kind that SITS somewhere and expiry of one that TRACKS it; every other line draws an em dash
   * instead of a control.
   *
   * <b>A BOOLEAN HERE WAS A DEFECT, and the footer prose did not cover it.</b> It drew the mark
   * unconditionally, so a grid holding nothing perishable — the ORDINARY case, since almost
   * nothing in the catalog tracks expiry — headed a column of pure em dashes `Expiry *` and let
   * the reader walk straight past it. The client gate was right and the server guard was right;
   * the HEADING was claiming an obligation no row on screen had. The footer sentence survives for
   * the MIXED grid, where an em dash sits beside a filled control and needs explaining.
   *
   * <b>Scoped to `lines`, never to the ticked rows.</b> A heading that appeared and vanished as
   * the reader ticked boxes would move every column under it while they worked.
   *
   * <b>The predicate and the CELL's own inline check must agree.</b> They are not shared because
   * the cells are positional JSX rather than a loop over this array — `line.sitsSomewhere` and
   * `line.tracksExpiry` are read in both places, so a third condition owes an edit here too.
   */
  required?: (line: GridLine) => boolean;
};

const LINE_COLUMNS: LineColumn[] = [
  { key: 'item', label: 'Item', width: 144, align: 'left', band: null },
  { key: 'expected', label: 'Exp. Qty', width: 74, align: 'right', band: null },
  { key: 'received', label: 'Received', width: 80, align: 'right', band: null },
  { key: 'good', label: 'Good', width: 72, align: 'right', band: null },
  { key: 'expired', label: 'Expired', width: 74, align: 'right', band: 'Unusable' },
  { key: 'damaged', label: 'Damaged', width: 80, align: 'right', band: 'Unusable' },
  { key: 'wrongItem', label: 'Wrong item', width: 86, align: 'right', band: 'Unusable' },
  { key: 'storage', label: 'Storage', width: 120, align: 'left', band: null, required: (l) => l.sitsSomewhere },
  { key: 'expiry', label: 'Expiry', width: 140, align: 'left', band: null, required: (l) => l.tracksExpiry },
  { key: 'unitCost', label: 'Unit cost', width: 86, align: 'right', band: null },
  { key: 'notes', label: 'Notes', width: 88, align: 'left', band: null },
];

/** The tick column, which is chrome rather than a resizable column. */
const SELECT_GUTTER = 40;

/**
 * The drag handle on a resizable header cell.
 *
 * Written once rather than at each of the eleven header cells — it appears on BOTH header rows
 * (a banded leaf is as resizable as an unbanded column), so two copies would be two chances for
 * the two rows to get a different grip.
 *
 * It carries `aria-label` and `role="separator"`, which is what `<InlineAddTable>`'s own grip
 * does: a control that can only be found with a pointer is one a keyboard reader cannot reach,
 * and a bare `<span>` with a pointer handler announces nothing at all.
 */
function ResizeGrip({ label, onStart }: { label: string; onStart: (e: React.PointerEvent) => void }) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label}`}
      onPointerDown={onStart}
      className="absolute -right-0.5 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-blue-300"
    />
  );
}

/**
 * The `*` on a required column's heading.
 *
 * Written once for the reason {@link ResizeGrip} is: it belongs on BOTH header rows — a banded
 * leaf can be as required as an unbanded column — and two copies would be two chances for the
 * two rows to mark one thing differently.
 *
 * <b>THE GLYPH IS `aria-hidden` AND THE WORD IS NOT</b>, which is the pairing every shared
 * input already draws. `<TextField>` hides its own `*` because the box underneath carries
 * `required`; <b>no `Td*` cell takes that prop</b>, so a bare hidden glyph here would mark the
 * column for a sighted reader and tell a screen reader nothing at all. A `<th>` in `<thead>` is
 * announced as the column's header, so the word lands when the reader enters the cell.
 */
function RequiredMark() {
  return (
    <>
      <span className="ml-0.5 text-red-600" aria-hidden="true">
        *
      </span>
      <span className="sr-only"> (required)</span>
    </>
  );
}

/**
 * Record a supplier delivery against a purchase order.
 *
 * <b>NOTHING HERE BECOMES STOCK.</b> The receipt is created pending inspection; each line is then
 * verified on the profile, and only the accepted quantity is registered. That gate is the whole
 * point of the module — a pallet on the dock and a pallet that has been counted are different
 * facts, and only the second is issuable.
 *
 * <b>EVERY LINE IS SENT, ticked or not.</b> An unticked row goes with a quantity of zero and the
 * server drops it after validating, so the index a rejection names is the index of the row on
 * screen. Filtering client-side would shift every later row's number and point the reader at the
 * wrong cell — the SALN schedule's rule, and the reason the cell ids below are positional.
 */
export function ReceiveDeliveryModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (receipt: GoodsReceiptDetail) => void;
}) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const [orders, setOrders] = useState<ReceivableOrderOption[]>([]);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderTab, setOrderTab] = useState<'all' | 'awaiting' | 'partial'>('all');
  const [order, setOrder] = useState<ReceivableOrderOption | null>(null);

  /**
   * WHERE IT CAME FROM, and the first thing the reader answers.
   *
   * It decides the shape of every step after it, which is why it is answered on step one and
   * why changing it CLEARS the counterparty and the grid: a draft carried across sources would
   * post lines that belong to an order the receipt no longer names.
   */
  const [source, setSource] = useState<GoodsReceiptSource>(GOODS_RECEIPT_SOURCE.purchaseOrder);
  const [donorName, setDonorName] = useState('');
  const [sourceFacilityId, setSourceFacilityId] = useState('');

  /** Every stock-holding item, for the donation and transfer grids. */
  const [items, setItems] = useState<InventoryItemOption[]>([]);
  const [pickedItemIds, setPickedItemIds] = useState<string[]>([]);

  const [lines, setLines] = useState<GridLine[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [rows, setRows] = useState<DraftLine[]>([]);

  const [facilities, setFacilities] = useState<StorageFacilityOption[]>([]);

  const [deliveredOn, setDeliveredOn] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [notes, setNotes] = useState('');

  // Reset on open, so a previous attempt never leaves a tick or a half-filled grid behind.
  useEffect(() => {
    if (!open) return;

    setStep(STEP.source);
    setConfirmed(false);
    setError(null);
    setSource(GOODS_RECEIPT_SOURCE.purchaseOrder);
    setDonorName('');
    setSourceFacilityId('');
    setPickedItemIds([]);
    setOrder(null);
    setOrderSearch('');
    setOrderTab('all');
    setLines([]);
    setRows([]);
    setDeliveredOn(new Date().toISOString().slice(0, 10));
    setInvoiceNumber('');
    setInvoiceDate('');
    setNotes('');
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();

    goodsReceiptsApi
      .receivableOrders(controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setOrders(found);
      })
      .catch(() => undefined);

    storageFacilitiesApi
      .options(controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setFacilities(found);
      })
      .catch(() => undefined);

    // Loaded ALWAYS rather than when a non-PO source is chosen: the feed is small, and a picker
    // that populates a beat after the reader lands on it reads as one that failed.
    inventoryItemsApi
      .options(controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setItems(found);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [open]);

  /**
   * ONE SEEDING, whichever source produced the grid line.
   *
   * An order line arrives with an outstanding quantity and an agreed price and is ticked with
   * both; a picked item has neither, so it is ticked with NOTHING in the received box — a
   * donation's quantity is what the reader counts on the dock, and a seeded figure there would
   * be a number nobody delivered.
   */
  const seedRow = useCallback(
    (line: GridLine): DraftLine => ({
      key: line.key,
      purchaseOrderItemId: line.purchaseOrderItemId,
      inventoryItemId: line.inventoryItemId,
      selected: true,
      quantityReceived: line.openQuantity,

      // ZERO rather than null: "nothing arrived unusable" is the ordinary delivery, and a
      // blank in three boxes on every row would read as three unanswered questions.
      quantityExpired: 0,
      quantityDamaged: 0,
      quantityWrongItem: 0,
      storageFacilityId: null,
      expiryDate: null,

      // SEEDED from the order's agreed price where there is one, and correctable — the lot's
      // ICS/PAR class is stamped from whatever is here, so a delivery invoiced differently
      // records its own. NULL on a donation, which was not bought.
      unitCost: line.unitCost,
      batchNumber: null,
      serials: line.tracksSerial && line.openQuantity ? Array(line.openQuantity).fill('') : [],
      notes: null,
    }),
    [],
  );

  /*
    Picking an order LOADS ITS OPEN LINES and seeds a row per line. Choosing a different order
    replaces them outright — a draft carried across would post lines that are not on the order
    the reader has just chosen, which the server refuses and the reader cannot see coming.
  */
  const chooseOrder = useCallback((next: ReceivableOrderOption) => {
    setOrder(next);
    setLines([]);
    setRows([]);
    setLoadingLines(true);

    goodsReceiptsApi
      .receivableLines(next.id)
      .then((found) => {
        const grid = found.map(gridLineOfOrder);

        setLines(grid);
        setRows(grid.map(seedRow));

        // Move on by choosing, which is the source's own behaviour: selecting an order IS the
        // answer to the counterparty step, so a second click on Next would be asking the same
        // question twice. It moves to the LINES rather than to a fixed index — the step after
        // the counterparty is what this means, and a number would be wrong the next time a step
        // is added.
        setStep(STEP.lines);
      })
      .catch((caught) =>
        setError(
          caught instanceof ApiError ? caught : new ApiError('Those lines could not be loaded.', 0),
        ),
      )
      .finally(() => setLoadingLines(false));
  }, [seedRow]);

  const visibleOrders = useMemo(() => {
    const term = orderSearch.trim().toLowerCase();

    return orders.filter((o) => {
      const matchesTab =
        orderTab === 'all'
          ? true
          : orderTab === 'awaiting'
            ? o.stage === PURCHASE_ORDER_STAGE.awaitingDelivery
            : o.stage === PURCHASE_ORDER_STAGE.partiallyReceived;

      if (!matchesTab) return false;
      if (!term) return true;

      return (
        o.orderNumber.toLowerCase().includes(term) ||
        o.supplierName.toLowerCase().includes(term) ||
        (o.items ?? '').toLowerCase().includes(term) ||
        (o.sourceRequestNumbers ?? '').toLowerCase().includes(term)
      );
    });
  }, [orders, orderSearch, orderTab]);

  /**
   * CHANGING THE SOURCE CLEARS EVERYTHING IT DECIDED.
   *
   * The counterparty and the grid both belong to the source that was chosen when they were
   * filled in, so carrying them across would post an order line on a donation — which the
   * server refuses and the reader cannot see coming. The same reasoning `chooseOrder` already
   * applies when a different order is picked.
   */
  const changeSource = useCallback((next: GoodsReceiptSource) => {
    setSource(next);
    setOrder(null);
    setDonorName('');
    setSourceFacilityId('');
    setPickedItemIds([]);
    setLines([]);
    setRows([]);
    setError(null);
  }, []);

  /**
   * THE ITEMS A DONATION OR A TRANSFER MAY NAME.
   *
   * Only what HOLDS STOCK — a real-property type is registered rather than received, and the
   * server refuses one outright, so offering it would be a picker listing something that cannot
   * be chosen. The frontend twin of `InventoryItemKinds.HoldsStock`.
   */
  const itemOptions = useMemo(
    () =>
      items
        .filter((i) => i.kind !== ITEM_KIND.realProperty)
        .map((i) => ({ value: i.id, label: i.name, sublabel: `${i.sku} · ${i.unitCode}`, keywords: i.sku })),
    [items],
  );

  /*
    THE PICKED ITEMS BECOME THE GRID, and a row already filled in SURVIVES being re-picked.

    Rebuilding every row on each tick would throw away a quantity the reader had already typed
    the moment they added a second item — so the rows are reconciled: what is still picked keeps
    its row, what is new gets a seeded one, and what was unpicked goes.
  */
  useEffect(() => {
    if (usesPurchaseOrder(source)) return;

    const picked = pickedItemIds
      .map((id) => items.find((i) => i.id === id))
      .filter((i): i is InventoryItemOption => i !== undefined)
      .map(gridLineOfItem);

    setLines(picked);
    setRows((current) => {
      const byKey = new Map(current.map((r) => [r.key, r]));
      return picked.map((line) => byKey.get(line.key) ?? seedRow(line));
    });
  }, [pickedItemIds, items, source, seedRow]);

  const lineByKey = useMemo(
    () => new Map(lines.map((l) => [l.key, l])),
    [lines],
  );

  const facilityOptions = useMemo(
    () => facilities.map((f) => ({ value: f.id, label: f.name, sublabel: f.path })),
    [facilities],
  );

  /*
    RESIZABLE COLUMNS, through the SHARED hook rather than a fourth implementation — product
    call, 01 Oct 2026. `useColumnWidths` already owns the persistence, the 56px floor, the
    viewport-bounded ceiling and the seed-from-the-MEASURED-width behaviour that stops a column
    jumping backwards on the first pixel of a drag.

    This grid is NOT an `<InlineAddTable>` and must not become one: its rows come from the
    purchase order, so a `+` could add nothing and a row's trash icon would offer to delete an
    order line. What it wants from that component is the resize, which lives in the hook.
  */
  const { widths: lineWidths, startResize: startLineResize } = useColumnWidths(
    'goods-receipt-lines-v2',
    LINE_COLUMNS,
  );

  const declaredWidth =
    SELECT_GUTTER + LINE_COLUMNS.reduce((sum, c, i) => sum + (lineWidths[i] ?? c.width), 0);

  /** The banded run, with each column's own index so a grip can still address it. */
  const bandedColumns = useMemo(
    () =>
      LINE_COLUMNS.map((column, index) => ({ column, index })).filter(({ column }) => column.band),
    [],
  );

  function patch(index: number, change: Partial<DraftLine>) {
    setRows((current) =>
      current.map((row, i) => {
        if (i !== index) return row;

        const next = { ...row, ...change };

        /*
          THE SERIAL BOXES FOLLOW THE QUANTITY, because a serialized line needs one per unit and
          the server refuses a mismatch. Resizing preserves what has already been typed rather
          than clearing it — a reader who corrects 3 to 4 has not changed their mind about the
          first three.
        */
        const line = lineByKey.get(row.key);

        if (line?.tracksSerial && change.quantityReceived !== undefined) {
          const size = Math.max(0, change.quantityReceived ?? 0);
          const kept = next.serials.slice(0, size);

          next.serials = [...kept, ...Array(Math.max(0, size - kept.length)).fill('')];
        }

        return next;
      }),
    );
  }

  /*
    THE FIRST STORE CHOSEN IS COPIED DOWN, which is the source's own behaviour and what its
    footnote promises. Only onto rows that have none yet and that actually sit somewhere — a
    licence is kept nowhere, so copying a store onto one would be a value the server refuses.
  */
  function chooseFacility(index: number, facilityId: string) {
    setRows((current) => {
      const alreadyAnswered = current.some((r, i) => i !== index && r.storageFacilityId);

      return current.map((row, i) => {
        if (i === index) return { ...row, storageFacilityId: facilityId };
        if (alreadyAnswered || row.storageFacilityId) return row;

        const line = lineByKey.get(row.key);

        return line?.sitsSomewhere ? { ...row, storageFacilityId: facilityId } : row;
      });
    });
  }

  const selected = rows.filter((r) => r.selected && (r.quantityReceived ?? 0) > 0);

  const lineIssues = useMemo(() => {
    const issues: string[] = [];

    for (const row of selected) {
      const line = lineByKey.get(row.key);
      if (!line) continue;

      const quantity = row.quantityReceived ?? 0;

      // ONLY AN ORDER CAPS A QUANTITY. A donation and a transfer were never ordered, so there
      // is nothing outstanding to be over — which is what `openQuantity: null` says.
      if (line.openQuantity !== null && quantity > line.openQuantity) {
        issues.push(`${line.sku} has only ${line.openQuantity} outstanding.`);
      }

      /*
        THE BREAKDOWN CANNOT EXCEED WHAT ARRIVED, which the server refuses too. Checked here so
        the reader meets it while they are still in the grid rather than on Submit — and the
        wording is the server's own, so the two surfaces say one thing.
      */
      const unusable = unusableOf(row);

      if (unusable > quantity) {
        issues.push(`${line.sku} breaks down ${unusable} as unusable but records only ${quantity} received.`);
      }

      if (line.sitsSomewhere && !row.storageFacilityId) {
        issues.push(`${line.sku} needs a store.`);
      }

      if (line.tracksExpiry && !row.expiryDate) {
        issues.push(`${line.sku} is perishable and needs an expiry.`);
      }

      if (line.tracksSerial && row.serials.filter((s) => s.trim()).length !== quantity) {
        issues.push(`${line.sku} needs ${quantity} serials.`);
      }
    }

    return issues;
  }, [selected, lineByKey]);

  /**
   * THE COUNTERPARTY THE CHOSEN SOURCE REQUIRES — the frontend twin of the three
   * `GoodsReceiptSources` predicates, and what step TWO is waiting for.
   */
  const counterpartyChosen = usesPurchaseOrder(source)
    ? order !== null
    : source === GOODS_RECEIPT_SOURCE.donation
      ? donorName.trim().length > 0
      : sourceFacilityId.length > 0;

  const steps = stepsFor(source);

  /*
    STEP ONE IS ALWAYS VALID, because a source is always chosen: it opens on `PurchaseOrder` and
    the radio group cannot be cleared. There is no state in which the reader is stuck on it, and
    a disabled Next there would be a control with nothing to act on.
  */
  const stepValid =
    step === STEP.source
      ? true
      : step === STEP.counterparty
        ? counterpartyChosen && (usesPurchaseOrder(source) || rows.length > 0)
        : step === STEP.lines
          ? selected.length > 0 && lineIssues.length === 0
          : step === STEP.delivery
            ? deliveredOn.length > 0
            : true;

  const missing =
    step === STEP.counterparty
      ? usesPurchaseOrder(source)
        ? 'Choose the purchase order this delivery is against.'
        : source === GOODS_RECEIPT_SOURCE.donation
          ? donorName.trim()
            ? 'Choose at least one item that was donated.'
            : 'Name who donated this.'
          : sourceFacilityId
            ? 'Choose at least one item that was transferred.'
            : 'Choose the store this stock came from.'
      : selected.length === 0
        ? 'Record a quantity against at least one line.'
        : (lineIssues[0] ?? 'Enter the day the delivery arrived.');

  const isLastStep = step === steps.length - 1;
  const canSave = stepValid && confirmed && !saving && deliveredOn.length > 0;

  /**
   * FILLS EVERY REQUIRED FIELD, whichever source is chosen — which is the contract: the primary
   * action enables on the FIRST try.
   *
   * <b>THE NON-PO PATH BUILDS ITS ROW SYNCHRONOUSLY, and that is the whole reason this branches
   * rather than calling the generator once.</b> A donation's grid is produced by an effect
   * watching the picked items, so a generator that merely picked one would hand `sampleDelivery`
   * an EMPTY grid, fill nothing in it, and leave Next disabled — which is exactly what it did
   * until this was LOOKED AT on screen. Building the line here means one click fills the whole
   * form, and the effect then reconciles by key and keeps the row rather than reseeding it.
   *
   * <b>THE SOURCE ITSELF IS LEFT ALONE.</b> It reveals and hides a whole block and re-feeds the
   * grid, so a generator that moved it would produce a different form on every click — the rule
   * the issuance generator already follows about its own method.
   */
  function autogenerate() {
    if (usesPurchaseOrder(source)) {
      const generated = sampleDelivery(orders, lines, facilities, rows);

      if (generated.order && !order) chooseOrder(generated.order);
      if (generated.rows) setRows(generated.rows);

      applyDeliveryDetails(generated);
      return;
    }

    // NEVER a field the reader has already decided.
    let workingLines = lines;
    let workingRows = rows;

    if (pickedItemIds.length === 0) {
      const pick = items.find((i) => i.kind !== ITEM_KIND.realProperty);

      if (pick) {
        const line = gridLineOfItem(pick);

        workingLines = [line];
        workingRows = [seedRow(line)];
        setPickedItemIds([pick.id]);
      }
    }

    const generated = sampleDelivery(orders, workingLines, facilities, workingRows);

    if (source === GOODS_RECEIPT_SOURCE.donation && !donorName.trim()) {
      setDonorName(generated.donorName);
    }

    if (source === GOODS_RECEIPT_SOURCE.stockTransfer && !sourceFacilityId) {
      // A REAL store, resolved against the live list — an id this picked itself would be one the
      // server refuses, which reads as the form being broken.
      const store = facilities[0];
      if (store) setSourceFacilityId(store.id);
    }

    if (generated.rows) setRows(generated.rows);

    applyDeliveryDetails(generated);
  }

  function applyDeliveryDetails(generated: ReturnType<typeof sampleDelivery>) {
    setDeliveredOn(generated.deliveredOn);
    setInvoiceNumber(generated.invoiceNumber);
    setInvoiceDate(generated.invoiceDate);
    setNotes(generated.notes);
  }

  async function save() {
    /*
      THE GUARD IS THE COUNTERPARTY, NOT THE ORDER.

      It read `if (!order) return;` until 02 Oct 2026, which made a donation and a transfer
      submit SILENTLY: the handler returned, the modal stayed open, and nothing anywhere said
      why — no banner, no request, no console error. The button was enabled, so there was not
      even a disabled reason to read.

      Found by SUBMITTING one rather than by reading this method, which is the only thing that
      could have found it: every check this product runs was green throughout.
    */
    if (!counterpartyChosen) return;

    setSaving(true);
    setError(null);

    try {
      // EVERY row, ticked or not — the index a rejection names is the index on screen.
      const payload: CreateGoodsReceiptLine[] = rows.map((row) => ({
        /*
          EXACTLY ONE OF THE TWO, never both — which is what the server refuses in both
          directions. A purchase-order line takes its item from the order, so sending one
          beside it would be the caller asserting two deliveries in one row.
        */
        purchaseOrderItemId: row.purchaseOrderItemId,
        inventoryItemId: row.purchaseOrderItemId ? null : row.inventoryItemId,
        storageFacilityId: row.selected ? row.storageFacilityId : null,
        batchNumber: row.batchNumber?.trim() || null,
        expiryDate: row.selected ? row.expiryDate || null : null,
        quantityReceived: row.selected ? (row.quantityReceived ?? 0) : 0,

        // ZEROED on an untouched row along with its quantity, so a reader who typed a cause and
        // then unticked the line cannot leave a breakdown behind on a line receiving nothing —
        // which the server refuses, and would refuse naming a cell the reader cannot see.
        quantityExpired: row.selected ? (row.quantityExpired ?? 0) : 0,
        quantityDamaged: row.selected ? (row.quantityDamaged ?? 0) : 0,
        quantityWrongItem: row.selected ? (row.quantityWrongItem ?? 0) : 0,
        unitCost: row.selected ? row.unitCost : null,
        notes: row.notes?.trim() || null,
        serials: row.selected
          ? row.serials.filter((s) => s.trim()).map((s) => ({ serialNumber: s.trim() }))
          : [],
      }));

      const saved = await goodsReceiptsApi.create({
        source,

        /*
          ONLY the field this source asks for. The other two are sent NULL rather than omitted,
          because the server REFUSES a counterparty a source does not name — it does not ignore
          one, so a value left over from a reader who changed their mind would be a rejection
          they could not see the cause of.
        */
        purchaseOrderId: usesPurchaseOrder(source) ? (order?.id ?? null) : null,
        donorName: source === GOODS_RECEIPT_SOURCE.donation ? donorName.trim() : null,
        sourceStorageFacilityId:
          source === GOODS_RECEIPT_SOURCE.stockTransfer ? sourceFacilityId : null,
        deliveredOn,
        invoiceNumber: invoiceNumber.trim() || null,
        invoiceDate: invoiceDate || null,
        notes: notes.trim() || null,
        lines: payload,
      });

      onSaved(saved);
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('The delivery could not be recorded.', 0);

      setError(failure);

      focusValidationError(failure, {
        idPrefix: 'goods-receipt',
        stepOf: (field) => {
          if (field.startsWith('lines')) return 1;
          if (field === 'purchaseOrderId') return 0;

          // deliveredOn, the three invoice fields and the notes.
          return 2;
        },
        onStep: setStep,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Receive Delivery"
      size="3xl"
      id="goods-receipt-modal"
      autogenerate={{ onGenerate: autogenerate, disabled: saving }}
      confirm={
        isLastStep
          ? {
              id: 'goods-receipt-confirm',
              checked: confirmed,
              onChange: setConfirmed,
              disabled: saving,
            }
          : undefined
      }
      footer={
        <>
          <Button id="goods-receipt-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {step > 0 ? (
            <Button
              id="goods-receipt-back"
              variant="secondary"
              onClick={() => setStep((s) => s - 1)}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Tooltip disabledReason={stepValid ? null : missing} block={false}>
              <Button
                id="goods-receipt-next"
                variant="secondary"
                onClick={() => setStep((s) => Math.min(s + 1, steps.length - 1))}
                disabled={!stepValid}
              >
                Next
              </Button>
            </Tooltip>
          ) : (
            <Tooltip disabledReason={canSave ? null : missing} block={false}>
              <Button
                id="goods-receipt-submit"
                variant="primary"
                onClick={() => void save()}
                disabled={!canSave}
                loading={saving}
                loadingLabel="Submitting"
              >
                Submit
              </Button>
            </Tooltip>
          )}
        </>
      }
    >
      <Stepper
        id="goods-receipt-steps"
        steps={steps.map((label) => ({ key: label, label }))}
        activeIndex={step}
        completedKeys={new Set(steps.slice(0, step))}
      />

      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {step === STEP.source ? (
          <div className="flex flex-col gap-4">
            {/*
              THE SOURCE LEADS, because it decides the shape of everything under it — which
              counterparty is asked for and where the lines come from. A radio group rather than
              a dropdown: three options the reader is choosing between is a set to see, not a
              list to open, and the choice is the first thing the form says.
            */}
            <RadioGroup
              id="goods-receipt-source"
              label="Where did this come from?"
              description="It decides what is asked for next — an order, a donor, or the store it came out of."
              required

              /*
                ONE PER ROW — product call, 05 Oct 2026. At two columns the three options read as
                a pair and a remainder, which is a shape the set does not have: they are three
                peers, and the odd one sat alone under the first looking like a lesser choice.
                Stacked, they read down as the three answers to the question above them, and each
                description gets the width to sit on one line.
              */
              columns={1}
              value={String(source)}
              onChange={(next) => changeSource(Number(next) as GoodsReceiptSource)}
              options={[
                {
                  value: String(GOODS_RECEIPT_SOURCE.purchaseOrder),
                  label: GOODS_RECEIPT_SOURCE_LABEL[GOODS_RECEIPT_SOURCE.purchaseOrder],
                  description: 'Against a purchase order already sent to a supplier.',
                },
                {
                  value: String(GOODS_RECEIPT_SOURCE.donation),
                  label: GOODS_RECEIPT_SOURCE_LABEL[GOODS_RECEIPT_SOURCE.donation],
                  description: 'Given to this organization. No order, and none is asked for.',
                },
                {
                  value: String(GOODS_RECEIPT_SOURCE.stockTransfer),
                  label: GOODS_RECEIPT_SOURCE_LABEL[GOODS_RECEIPT_SOURCE.stockTransfer],
                  description: 'Arrived from another of this organization’s own stores.',
                },
              ]}
            />
          </div>
        ) : null}

        {step === STEP.counterparty ? (
          <div className="flex flex-col gap-4">
            {usesPurchaseOrder(source) ? null : (
              <div className="flex flex-col gap-4">
                {source === GOODS_RECEIPT_SOURCE.donation ? (
                  <TextField
                    id="goods-receipt-donor-name"
                    label="Donor"
                    required
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    placeholder="Who gave it"
                    description="Free text — a donor is not a supplier, so it does not go in that catalog."
                    error={error?.fieldError('donorName')}
                  />
                ) : (
                  <>
                    <Select
                      id="goods-receipt-source-storage-facility-id"
                      label="From store"
                      required
                      value={sourceFacilityId}
                      onChange={setSourceFacilityId}
                      options={facilityOptions}
                      placeholder="Choose the store it came from"
                      description="One of this organization’s own stores."
                      error={error?.fieldError('sourceStorageFacilityId')}
                    />

                    {/*
                      THE COST OF HAVING NO TRANSFER RECORD, stated before the tick rather than
                      discovered afterwards. Registering this creates stock HERE and removes none
                      THERE, so the same units sit at both stores until somebody adjusts the
                      source down. Saying it on the form is what makes that a decision the reader
                      takes rather than a surprise they meet on the stock levels screen.
                    */}
                    <NextActionAlert
                      icon={<TriangleAlert className="h-4 w-4" aria-hidden />}
                      title="Nothing leaves the source store"
                      description="This records what arrived. It does not take the stock out of the store it came from — do that with a stock adjustment there, or the units are counted in both places."
                    />
                  </>
                )}

                {/*
                  THE ITEMS, picked freely, because there is no order to take them from. The grid
                  on the next step is built from what is chosen here.
                */}
                <MultiSelect
                  id="goods-receipt-items"
                  label="What arrived"
                  required
                  value={pickedItemIds}
                  onChange={setPickedItemIds}
                  options={itemOptions}
                  placeholder="Choose the items delivered"
                  description="Only items that hold stock — a property type is registered rather than received."
                />
              </div>
            )}

            {usesPurchaseOrder(source) ? (
              <div className="flex flex-col gap-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Source purchase order
              </p>
              <p className="pt-1 text-sm text-slate-600">
                Pick the purchase order the delivery is against — selecting it moves you to the
                line items.
              </p>
            </div>

            <TextField
              id="goods-receipt-order-search"
              label="Search"
              labelHidden
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              placeholder="Search receivable purchase orders…"
            />

            <div className="flex gap-2">
              {(
                [
                  ['all', 'All'],
                  ['awaiting', 'Awaiting delivery'],
                  ['partial', 'Partially received'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setOrderTab(key)}
                  className={
                    orderTab === key
                      ? 'rounded-full border border-slate-400 bg-blue-50 px-3 py-1 text-xs font-medium text-slate-900'
                      : 'rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 hover:bg-slate-50'
                  }
                >
                  {label}
                </button>
              ))}
            </div>

            {visibleOrders.length === 0 ? (
              <p className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                {orders.length === 0
                  ? /*
                      THE GATE STATED WHERE THE READER MEETS IT. An approved order is deliberately
                      not listed: it is receivable only once somebody has recorded that the
                      document went out, so the sentence names the missing step rather than
                      leaving a reader hunting for an order they can plainly see on the register.
                    */
                    'No purchase order that has gone out to the supplier has anything outstanding. An approved order has to be marked as sent on its own page before a delivery can be received against it.'
                  : 'No order matches that search.'}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {visibleOrders.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => chooseOrder(o)}
                      disabled={loadingLines}
                      className={
                        order?.id === o.id
                          ? 'flex w-full items-center gap-3 rounded-lg border border-slate-400 bg-blue-50 p-3 text-left'
                          : 'flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left hover:bg-slate-50'
                      }
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-slate-900">
                          {o.supplierName}
                        </span>
                        <span className="block truncate pt-0.5 font-mono text-xs text-slate-600">
                          {o.orderNumber}
                          {o.sourceRequestNumbers ? ` → ${o.sourceRequestNumbers}` : ''}
                          {o.items ? ` · ${o.items}` : ''}
                          {` · ${o.openLineCount} of ${o.lineCount} open`}
                        </span>
                      </span>

                      <span className="flex shrink-0 items-center gap-2">
                        <span className="font-mono text-xs tabular-nums text-slate-600">
                          {o.receivedPercent.toFixed(2)}%
                        </span>
                        <span
                          className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200"
                          aria-hidden="true"
                        >
                          <span
                            className="block h-full rounded-full bg-blue-500"
                            style={{ width: `${Math.min(100, o.receivedPercent)}%` }}
                          />
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
              </div>
            ) : null}
          </div>
        ) : null}

        {step === STEP.lines ? (
          <div className="flex flex-col gap-3">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Lines to receive
            </p>

            <div className="modern-scrollbar overflow-x-auto rounded-lg border border-slate-200">
              {/*
                FIXED LAYOUT with a `<colgroup>`, which is what makes the columns DRAGGABLE: a
                column can only be sized to a number the table honours, and `table-layout: auto`
                sizes from content instead. The floor is the declared sum, so a table wider than
                the modal scrolls rather than squeezing every column — the rule
                `<InlineAddTable>` records at length.
              */}
              <table
                className="border-collapse text-sm"
                style={{ tableLayout: 'fixed', width: '100%', minWidth: `${declaredWidth}px` }}
              >
                <colgroup>
                  <col style={{ width: SELECT_GUTTER }} />
                  {LINE_COLUMNS.map((column, index) => (
                    <col key={column.key} style={{ width: lineWidths[index] ?? column.width }} />
                  ))}
                </colgroup>

                {/*
                  A BANDED HEADER, because the reader's own structure is hierarchical: the
                  received quantity splits into Good and Unusable, and Unusable splits into three
                  causes. Seven numeric columns in an undifferentiated row say none of that.

                  An UNBANDED column spans both rows as one tall cell and is `align-bottom`, so
                  every column name shares a baseline — left at the default `middle` the unbanded
                  names float a row-height above the banded ones, which is the one thing a banded
                  header exists to prevent.
                */}
                <thead className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600">
                  <tr className="[&>th]:border-l [&>th]:border-slate-200 [&>th]:px-2 [&>th]:py-2 [&>th]:font-normal [&>th]:first:border-l-0">
                    <th rowSpan={2} className="align-bottom" />

                    {/*
                      WALKED IN COLUMN ORDER, and that is load-bearing rather than tidy: the band
                      sits BETWEEN `good` and `storage`, so emitting every unbanded cell first and
                      appending the band would draw it after Notes. A `<tr>` renders in document
                      order and has no way to be told otherwise.

                      The band cell is emitted at the position of its FIRST column and the rest of
                      the band is skipped, so the colspan and the leaves below come from one array
                      — a hand-kept colspan is the second list a new column goes missing from.
                    */}
                    {LINE_COLUMNS.map((column, index) => {
                      if (!column.band) {
                        return (
                          <th
                            key={column.key}
                            rowSpan={2}
                            className={`relative align-bottom ${
                              column.align === 'right' ? 'text-right' : 'text-left'
                            }`}
                          >
                            {column.label}
                            {column.required && lines.some(column.required) ? <RequiredMark /> : null}
                            <ResizeGrip
                              label={column.label}
                              onStart={(e) => startLineResize(index, e)}
                            />
                          </th>
                        );
                      }

                      if (bandedColumns[0]?.index !== index) return null;

                      return (
                        <th
                          key={column.band}
                          colSpan={bandedColumns.length}
                          className="border-l border-slate-400 text-center align-bottom"
                        >
                          {column.band}
                        </th>
                      );
                    })}
                  </tr>

                  <tr className="[&>th]:border-t [&>th]:border-t-slate-300 [&>th]:px-2 [&>th]:py-2 [&>th]:font-normal">
                    {bandedColumns.map(({ column, index }, position) => (
                      <th
                        key={column.key}
                        className={`relative text-right ${
                          position === 0
                            ? 'border-l border-slate-400'
                            : 'border-l border-slate-200'
                        }`}
                      >
                        {column.label}
                        {column.required && lines.some(column.required) ? <RequiredMark /> : null}
                        <ResizeGrip label={column.label} onStart={(e) => startLineResize(index, e)} />
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, index) => {
                    const line = lineByKey.get(row.key);
                    if (!line) return null;

                    return (
                      <tr
                        key={row.key}
                        className="border-t border-slate-200 align-middle"
                      >
                        <td className="px-2 py-1 text-center">
                          <Checkbox
                            id={`goods-receipt-lines.${index}.selected`}
                            checked={row.selected}
                            onChange={(e) =>
                              patch(index, {
                                selected: e.target.checked,
                                quantityReceived: e.target.checked ? line.openQuantity : 0,

                                /*
                                  UNTICKING CLEARS THE BREAKDOWN TOO. A reader who recorded two
                                  damaged and then decided the line was not on this delivery
                                  would otherwise leave a cause behind against a quantity of
                                  zero — which the server refuses, naming a cell that is now
                                  disabled and reads as untouched.
                                */
                                ...(e.target.checked
                                  ? {}
                                  : { quantityExpired: 0, quantityDamaged: 0, quantityWrongItem: 0 }),
                              })
                            }
                            aria-label={`Receive ${line.sku}`}
                          />
                        </td>

                        {/*
                          ONE child, not two. `<TdSeamless>` lays its children out in a FLEX ROW,
                          so two sibling spans become two flex items and sit side by side however
                          `block` they declare themselves — the name and the SKU rendered as
                          "AI SubscriptionITEM-UYGM7P". It is the same trap that component's own
                          remarks document about `align="right"`, and it was found by looking at
                          the screen rather than at the markup.
                        */}
                        <TdSeamless>
                          <span className="min-w-0">
                            <span className="block truncate text-slate-600">{line.itemName}</span>
                            <span className="block truncate font-mono text-[11px] text-slate-400">
                              {line.sku}
                            </span>
                          </span>
                        </TdSeamless>

                        <TdSeamless align="right">
                          <span className="font-mono tabular-nums text-slate-500">
                            {line.openQuantity} {line.unitCode}
                          </span>
                        </TdSeamless>

                        <TdNumber
                          value={row.quantityReceived}
                          onChange={(next) => patch(index, { quantityReceived: next })}
                          allowNull
                          integerOnly
                          min={0}
                          max={line.openQuantity ?? undefined}
                          disabled={!row.selected}
                          id={`goods-receipt-lines.${index}.quantityReceived`}
                          ariaLabel={`Quantity received for ${line.sku}`}
                        />

                        {/*
                          GOOD IS STATED, NEVER ASKED FOR. It is received minus the three causes,
                          so a box for it would be a second answer to a question the row already
                          settles — and the server derives the same figure, which is what stops
                          the two disagreeing.

                          It goes RED when the breakdown exceeds what arrived, because that is the
                          one state where the number on screen is not a quantity anybody has: the
                          reader sees the contradiction in the cell rather than only in the
                          disabled Next button's tooltip.
                        */}
                        <TdSeamless align="right">
                          <span
                            className={`font-mono tabular-nums ${
                              goodOf(row) < 0
                                ? 'text-red-600'
                                : row.selected
                                  ? 'text-slate-600'
                                  : 'text-slate-300'
                            }`}
                          >
                            {goodOf(row)}
                          </span>
                        </TdSeamless>

                        <TdNumber
                          value={row.quantityExpired}
                          onChange={(next) => patch(index, { quantityExpired: next })}
                          allowNull
                          integerOnly
                          min={0}
                          max={line.openQuantity ?? undefined}
                          disabled={!row.selected}
                          id={`goods-receipt-lines.${index}.quantityExpired`}
                          ariaLabel={`Quantity expired for ${line.sku}`}
                        />

                        <TdNumber
                          value={row.quantityDamaged}
                          onChange={(next) => patch(index, { quantityDamaged: next })}
                          allowNull
                          integerOnly
                          min={0}
                          max={line.openQuantity ?? undefined}
                          disabled={!row.selected}
                          id={`goods-receipt-lines.${index}.quantityDamaged`}
                          ariaLabel={`Quantity damaged for ${line.sku}`}
                        />

                        <TdNumber
                          value={row.quantityWrongItem}
                          onChange={(next) => patch(index, { quantityWrongItem: next })}
                          allowNull
                          integerOnly
                          min={0}
                          max={line.openQuantity ?? undefined}
                          disabled={!row.selected}
                          id={`goods-receipt-lines.${index}.quantityWrongItem`}
                          ariaLabel={`Wrong-item quantity for ${line.sku}`}
                        />

                        {line.sitsSomewhere ? (
                          <TdSelect
                            value={row.storageFacilityId}
                            onChange={(next) => chooseFacility(index, next)}
                            options={facilityOptions}
                            disabled={!row.selected}
                            id={`goods-receipt-lines.${index}.storageFacilityId`}
                            ariaLabel={`Storage for ${line.sku}`}
                          />
                        ) : (
                          /*
                            A LICENCE IS KEPT NOWHERE, so the cell states that rather than
                            offering a picker the server refuses. An absence, not a disabled
                            control: the question does not apply to this kind at all.
                          */
                          <TdSeamless>
                            <span className="text-slate-400">—</span>
                          </TdSeamless>
                        )}

                        {line.tracksExpiry ? (
                          <TdDate
                            value={row.expiryDate ?? ''}
                            onChange={(next) => patch(index, { expiryDate: next || null })}
                            disabled={!row.selected}
                            id={`goods-receipt-lines.${index}.expiryDate`}
                            ariaLabel={`Expiry for ${line.sku}`}
                          />
                        ) : (
                          <TdSeamless>
                            <span className="text-slate-400">—</span>
                          </TdSeamless>
                        )}

                        <TdMoney
                          value={row.unitCost}
                          onChange={(next) => patch(index, { unitCost: next })}
                          disabled={!row.selected}
                          id={`goods-receipt-lines.${index}.unitCost`}
                          ariaLabel={`Unit cost for ${line.sku}`}
                        />

                        {/*
                          THE LINE'S OWN NOTE, which the source collects too. It was on the
                          request and the row from the first revision and drawn by NOTHING —
                          a value collected, stored and never read, which is the
                          `ExpiryThresholdDays` failure in its cheapest form. The profile's
                          line table shows it, so it is written here and read there.
                        */}
                        <TdInput
                          value={row.notes ?? ''}
                          onChange={(next) => patch(index, { notes: next || null })}
                          disabled={!row.selected}
                          placeholder="optional"
                          id={`goods-receipt-lines.${index}.notes`}
                          ariaLabel={`Notes for ${line.sku}`}
                        />
                      </tr>
                    );
                  })}

                  {rows.map((row, index) => {
                    const line = lineByKey.get(row.key);

                    if (!line?.tracksSerial || !row.selected || row.serials.length === 0) {
                      return null;
                    }

                    return (
                      <tr
                        key={`${row.purchaseOrderItemId}-serials`}
                        className="border-t border-slate-200 bg-slate-50"
                      >
                        <td />
                        {/* DERIVED from the column list, so a column added later cannot leave
                            this span short and tear a hole in the serial row. */}
                        <td colSpan={LINE_COLUMNS.length} className="px-2 py-2">
                          <p className="pb-1 text-[11px] text-slate-500">
                            Serial / asset tags for{' '}
                            <span className="font-mono">{line.sku}</span> — one per unit, each
                            becomes an assignable stock unit
                            <span className="pl-1 font-mono tabular-nums">
                              ({row.serials.filter((s) => s.trim()).length}/{row.serials.length})
                            </span>
                          </p>

                          <div className="flex flex-wrap gap-2">
                            {row.serials.map((serial, serialIndex) => (
                              <span
                                key={serialIndex}
                                className="flex items-center overflow-hidden rounded-lg border border-slate-300 bg-white"
                              >
                                <span className="border-r border-slate-200 px-2 py-1 font-mono text-[11px] text-slate-400">
                                  #{String(serialIndex + 1).padStart(2, '0')}
                                </span>
                                <input
                                  id={`goods-receipt-lines.${index}.serials.${serialIndex}.serialNumber`}
                                  aria-label={`Serial ${serialIndex + 1} for ${line.sku}`}
                                  value={serial}
                                  onChange={(event) =>
                                    patch(index, {
                                      serials: row.serials.map((s, i) =>
                                        i === serialIndex ? event.target.value : s,
                                      ),
                                    })
                                  }
                                  placeholder="Serial no. or asset tag"
                                  className="w-52 bg-transparent px-2 py-1 font-mono text-xs text-slate-600 outline-none"
                                />
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-slate-500">
              The first storage facility you choose is copied to every line — adjust any line
              individually after. Batch numbers are assigned when left blank. A column marked{' '}
              <b>*</b> is required on every line that draws a control for it — a line kept
              nowhere, or one that never expires, reads an em dash instead. Quantities cannot
              exceed each line&apos;s open amount. <b>Good</b> is what is left once the unusable
              counts are taken off, so it is shown rather than typed. This breakdown is what{' '}
              <b>you</b> found on the dock — it is recorded against the delivery and{' '}
              <b>nothing becomes stock yet</b>: each line still goes through the official
              inspection before the receipt is registered.
            </p>
          </div>
        ) : null}

        {step === STEP.delivery ? (
          <div className="flex flex-col gap-4">
            <DateField
              id="goods-receipt-deliveredOn"
              label="Delivered on"
              required
              value={deliveredOn}
              onChange={setDeliveredOn}
              max={new Date().toISOString().slice(0, 10)}
              description="The day the goods arrived. A delivery is something that has happened, so it cannot be dated forward."
            />

            <div>
              <p className="pb-2 text-xs font-medium uppercase tracking-wider text-slate-500">
                Invoice (optional)
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  id="goods-receipt-invoiceNumber"
                  label="Invoice #"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  description="Goods routinely arrive ahead of the invoice, so both are optional."
                />

                <DateField
                  id="goods-receipt-invoiceDate"
                  label="Invoice date"
                  value={invoiceDate}
                  onChange={setInvoiceDate}
                  max={new Date().toISOString().slice(0, 10)}
                />
                {/*
                  THERE IS NO INVOICE AMOUNT FIELD, and the absence is a product call
                  (05 Oct 2026): what a reader wants beside a delivery is what the ORDER comes
                  to, which the purchase order already states — so the profile derives it and
                  nobody types it. The two fields left fill one row of this grid exactly.
                */}
              </div>
            </div>

            <Textarea
              id="goods-receipt-notes"
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes for this receipt…"
            />
          </div>
        ) : null}

        {step === STEP.summary ? (
          <div className="flex flex-col gap-4">
            {order ? (
              <div className="rounded-lg border border-slate-400 bg-blue-50 p-3">
                <p className="text-sm font-medium text-slate-900">{order.supplierName}</p>
                <p className="pt-0.5 font-mono text-xs text-slate-600">
                  {order.orderNumber}
                  {order.sourceRequestNumbers ? ` → ${order.sourceRequestNumbers}` : ''}
                </p>
              </div>
            ) : null}

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>tr>th]:border-l [&>tr>th]:border-slate-200 [&>tr>th]:px-2 [&>tr>th]:py-2 [&>tr>th]:font-normal [&>tr>th]:first:border-l-0">
                  <tr>
                    <th className="text-left">Item</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Balance</th>
                    <th className="text-left">Storage</th>
                    <th className="text-right">Line total</th>
                  </tr>
                </thead>

                <tbody>
                  {selected.map((row) => {
                    const line = lineByKey.get(row.key)!;
                    const quantity = row.quantityReceived ?? 0;
                    // NO BALANCE WITHOUT AN ORDER — it is what is left ON one.
                    const balance =
                      line.openQuantity === null ? null : Math.max(0, line.openQuantity - quantity);
                    const facility = facilities.find((f) => f.id === row.storageFacilityId);

                    return (
                      <tr
                        key={row.purchaseOrderItemId}
                        className="border-t border-slate-200 align-middle [&>td]:border-l [&>td]:border-slate-200 [&>td]:px-2 [&>td]:py-2 [&>td]:first:border-l-0"
                      >
                        <td>
                          <span className="block text-slate-600">{line.itemName}</span>
                          <span className="block font-mono text-[11px] text-slate-400">
                            {line.sku}
                          </span>
                          {row.serials.filter((s) => s.trim()).length > 0 ? (
                            <span className="block pt-0.5 font-mono text-[11px] text-blue-500">
                              {row.serials.filter((s) => s.trim()).join(', ')}
                            </span>
                          ) : null}
                        </td>

                        <td className="text-right font-mono tabular-nums text-slate-500">
                          {quantity} {line.unitCode}
                        </td>

                        {/*
                          BALANCE is what stays OPEN on the order line once this is registered —
                          and it is what would be open if everything on this delivery were
                          ACCEPTED. Inspection can only ever leave it higher, never lower.
                        */}
                        <td className="text-right font-mono tabular-nums">
                          <span className={balance === 0 ? 'text-emerald-600' : 'text-slate-500'}>
                            {balance === null ? '—' : `${balance} ${line.unitCode}`}
                          </span>
                        </td>

                        <td className="text-slate-500">{facility?.name ?? '—'}</td>

                        <td className="text-right font-mono tabular-nums text-slate-500">
                          {row.unitCost === null ? '—' : formatMoney(row.unitCost * quantity)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot className="bg-slate-100 [&>tr>td]:border-l [&>tr>td]:border-slate-300 [&>tr>td]:px-2 [&>tr>td]:py-2 [&>tr>td]:first:border-l-0">
                  <tr>
                    <td colSpan={4} className="text-right text-[9px] uppercase tracking-wider text-gray-600">
                      Total
                    </td>
                    <td className="text-right font-mono tabular-nums text-slate-600">
                      {formatMoney(
                        selected.reduce(
                          (sum, row) => sum + (row.unitCost ?? 0) * (row.quantityReceived ?? 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <p className="text-xs text-slate-500">
              Balance is each line&apos;s open quantity still outstanding once this receipt is
              registered — a fully-received line reads <span className="text-emerald-600">0</span>.
              A rejected quantity at inspection leaves it open for the supplier to re-deliver.
            </p>

            <PreviewTable
              variant="table-display"
              rows={[
                { label: 'Delivered on', value: deliveredOn ? formatDateOnly(deliveredOn) : null, mono: true },
                { label: 'Invoice #', value: invoiceNumber.trim() || null, mono: true },
                { label: 'Invoice date', value: invoiceDate ? formatDateOnly(invoiceDate) : null, mono: true },
                { label: 'Notes', value: notes.trim() || null },
              ]}
            />

            <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              Recording this delivery creates a receipt <b>pending inspection</b>. No stock is
              created and the order is not updated until each line has been verified and the
              receipt registered.
            </p>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
